/**
 * A tiny registry pages use to publish "what was on screen" for feedback
 * reports.
 *
 * This client has no global store — state lives in per-page React state
 * plus a couple of contexts — so there is no single object to serialise
 * when a user files a report. Rather than invent one, each page opts in
 * with `useFeedbackPageContext({ ... })` and publishes a small, curated
 * object. Pages that never opt in simply contribute nothing.
 *
 * Deliberately outside React state: the widget reads this at submit time,
 * and a context value that re-rendered every consumer on each page change
 * would cost more than it returns.
 */

export type FeedbackPageContext = Record<string, unknown>;

let current: FeedbackPageContext = {};

/** Replace the published context. Called by the hook on mount/change. */
export function publishFeedbackPageContext(value: FeedbackPageContext): void {
  current = value;
}

/**
 * Clear on unmount, but only if this page is still the publisher. Without
 * the identity check, unmount ordering during a route change (new page
 * mounts before old page unmounts) would wipe the incoming page's context.
 */
export function clearFeedbackPageContext(value: FeedbackPageContext): void {
  if (current === value) current = {};
}

export function readFeedbackPageContext(): FeedbackPageContext {
  return { ...current };
}
